/**
 * Converts API line items + construction method data into CabinetData for the 3D viewer.
 */

import type { CabinetData, ConstructionConfig } from './types';
import { DEFAULT_COLORS } from './materials';

/** Default construction config matching ConstructionMethod entity defaults */
const DEFAULT_CONFIG: ConstructionConfig = {
  has_left_panel: true,
  has_right_panel: true,
  has_bottom_panel: true,
  top_style: 'stretchers',
  top_stretcher_qty: 2,
  top_stretcher_width: 3.5,
  has_back_panel: true,
  back_nailer_qty: 2,
  back_nailer_width: 3.5,
  middle_stretcher_width: 3.0,
  toe_kick_style: 'detached',
  toe_kick_height: 4.0,
  door_overlay_top: 0,
  door_overlay_bottom: 0,
  door_overlay_left: 0,
  door_overlay_right: 0,
  door_gap: 0.125,
  door_reveal: 0.125,
  has_scribe_strips: false,
  scribe_strip_width: 1.5,
};

/**
 * Build a ConstructionConfig from a construction method object + per-item overrides.
 */
function buildConfig(
  method: Record<string, unknown> | null | undefined,
  overridesJson: string,
): ConstructionConfig {
  const base = { ...DEFAULT_CONFIG };

  // Apply construction method fields
  if (method) {
    for (const key of Object.keys(base) as (keyof ConstructionConfig)[]) {
      if (key in method && method[key] != null) {
        (base as Record<string, unknown>)[key] = method[key];
      }
    }
  }

  // Apply per-item construction overrides
  try {
    const overrides = JSON.parse(overridesJson || '{}');
    for (const key of Object.keys(overrides)) {
      if (key in base && overrides[key] != null) {
        (base as Record<string, unknown>)[key] = overrides[key];
      }
    }
  } catch { /* ignore parse errors */ }

  return base;
}

/**
 * Convert a line item API response into CabinetData for the viewer.
 * The `constructionMethods` map is keyed by construction method ID.
 */
export function lineItemToCabinetData(
  item: Record<string, unknown>,
  constructionMethods: Map<string, Record<string, unknown>>,
): CabinetData | null {
  const w = item.width as number | null;
  const h = item.height as number | null;
  const d = item.depth as number | null;
  if (!w || !h || !d) return null; // Can't render without dimensions

  const cmId = item.construction_method_id as string | null;
  const method = cmId ? constructionMethods.get(cmId) ?? null : null;

  return {
    id: item.id as string,
    code: (item.cabinet_code as string | null) ?? null,
    description: item.description as string,
    width: w,
    height: h,
    depth: d,
    position_x: (item.position_x as number) ?? 0,
    position_y: (item.position_y as number) ?? 0,
    position_z: (item.position_z as number) ?? 0,
    num_doors: (item.num_doors as number) ?? 0,
    num_drawers: (item.num_drawers as number) ?? 0,
    num_shelves: (item.num_shelves as number) ?? 1,
    quantity: (item.quantity as number) ?? 1,
    face_component_config: (item.face_component_config as string) ?? '{}',
    construction_method_id: cmId,
    material_overrides: (item.material_overrides as string) ?? '{}',
    // Resolve colors from loaded material relations
    box_color: extractMaterialColor(item, 'box_material') ?? DEFAULT_COLORS.box,
    door_color: extractMaterialColor(item, 'door_material') ?? DEFAULT_COLORS.door,
    back_color: extractMaterialColor(item, 'back_material') ?? DEFAULT_COLORS.back,
    shelf_color: extractMaterialColor(item, 'shelf_material') ?? DEFAULT_COLORS.shelf,
    stretcher_color: DEFAULT_COLORS.stretcher, // stretcher material not on line item
    config: buildConfig(method, (item.construction_overrides as string) ?? '{}'),
  };
}

function extractMaterialColor(item: Record<string, unknown>, relationKey: string): string | null {
  const mat = item[relationKey] as Record<string, unknown> | null | undefined;
  if (!mat) return null;
  const color = mat.color as string | null;
  return color || null;
}

/**
 * Detect adjacency between cabinets for reveal calculations.
 */
export function detectAdjacency(
  cab: CabinetData,
  allCabs: CabinetData[],
): { left: boolean; right: boolean } {
  const THRESHOLD = 1.0; // 1 inch gap tolerance
  let left = false;
  let right = false;

  for (const other of allCabs) {
    if (other.id === cab.id) continue;
    // Check if same Z row (within threshold)
    if (Math.abs(other.position_z - cab.position_z) > THRESHOLD) continue;

    // Check right adjacency: other starts where this one ends
    if (Math.abs((cab.position_x + cab.width) - other.position_x) < THRESHOLD) {
      right = true;
    }
    // Check left adjacency: other ends where this one starts
    if (Math.abs((other.position_x + other.width) - cab.position_x) < THRESHOLD) {
      left = true;
    }
  }

  return { left, right };
}
